import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  FaCalendarAlt, 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlus,
  FaCalendarWeek,
  FaCalendarDay,
  FaCalendarCheck
} from 'react-icons/fa';
import MonthlyStatsHeader from './MonthlyStatsHeader';

const CalendarHeader = ({
  selectedDate,
  currentView = 'week',
  onDateChange,
  onViewChange,
  onWeekNavigate,
  onQuickCreate,
  onNotificationsToggle,
  unreadCount,
  eventiOggi,
  // ✅ NEW: Props per filtri tipi evento
  eventTypes = [],
  visibleEventTypes = new Set(),
  onEventTypeToggle,
  isLoadingEventTypes = false
}) => {
  const { theme } = useTheme();

  // Navigation functions
  const navigatePrevious = () => {
    const newDate = new Date(selectedDate);
    
    switch (currentView) {
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        onWeekNavigate?.('previous');
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() - 1);
        break;
      default:
        newDate.setDate(newDate.getDate() - 1);
    }
    
    onDateChange(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(selectedDate);
    
    switch (currentView) {
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        onWeekNavigate?.('next');
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + 1);
        break;
      default:
        newDate.setDate(newDate.getDate() + 1);
    }
    
    onDateChange(newDate);
  };

  const navigateToday = () => {
    onDateChange(new Date());
  };

  // Format display date based on current view
  const formatDisplayDate = () => {
    const options = { locale: 'it-IT' };
    
    switch (currentView) {
      case 'week':
        const weekStart = new Date(selectedDate);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        if (weekStart.getMonth() === weekEnd.getMonth()) {
          return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekEnd.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`;
        } else {
          return `${weekStart.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        }
        
      case 'month':
        return selectedDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
        
      case 'year':
        return `Anno ${selectedDate.getFullYear()}`;
        
      default:
        return selectedDate.toLocaleDateString('it-IT', { 
          weekday: 'long', 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        });
    }
  };

  // View options
  const viewOptions = [
    { 
      key: 'week', 
      label: 'Settimana', 
      icon: FaCalendarWeek,
      description: 'Vista settimanale dettagliata'
    },
    { 
      key: 'month', 
      label: 'Mese', 
      icon: FaCalendarAlt,
      description: 'Vista mensile con multi-select'
    },
    { 
      key: 'year', 
      label: 'Anno', 
      icon: FaCalendarCheck,
      description: 'Vista annuale per pianificazione'
    }
  ];

  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className={`${cardBg} rounded-lg border ${borderColor} shadow-sm`}>
      {/* Main header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          {/* Left section - Title and date navigation */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <FaCalendarAlt className="text-2xl text-blue-600" />
              <div>
                <h1 className={`text-xl font-bold ${textColor}`}>
                  Calendario
                </h1>
                <p className={`text-sm ${mutedText}`}>
                  {eventiOggi > 0 ? `${eventiOggi} eventi oggi` : 'Nessun evento oggi'}
                </p>
              </div>
            </div>

            {/* Date navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={navigatePrevious}
                className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${mutedText} hover:${textColor}`}
                title="Periodo precedente"
              >
                <FaChevronLeft />
              </button>
              
              <div className="mx-2 text-center min-w-[200px]">
                <div className={`font-semibold ${textColor}`}>
                  {formatDisplayDate()}
                </div>
              </div>
              
              <button
                onClick={navigateNext}
                className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${mutedText} hover:${textColor}`}
                title="Periodo successivo"
              >
                <FaChevronRight />
              </button>
            </div>

            {/* Today button */}
            <button
              onClick={navigateToday}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Oggi
            </button>
          </div>

          {/* Right section - Actions */}
          <div className="flex items-center gap-3">
            {/* New event button */}
            <button
              onClick={onQuickCreate}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <FaPlus className="w-4 h-4" />
              Nuovo Evento
            </button>
          </div>
        </div>
      </div>

      {/* View selector */}
      <div className="p-4">
        <div className="flex flex-wrap gap-2">
          {viewOptions.map((option) => {
            const Icon = option.icon;
            const isActive = currentView === option.key;
            
            return (
              <button
                key={option.key}
                onClick={() => onViewChange(option.key)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-blue-600 text-white shadow-lg scale-105' 
                    : `${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} ${textColor}`
                  }
                `}
                title={option.description}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{option.label}</span>
              </button>
            );
          })}
        </div>

        {/* ✅ NEW: Filtri tipi evento (solo per vista mensile) */}
        {currentView === 'month' && eventTypes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-4 mb-3">
              <h3 className={`text-sm font-medium ${textColor}`}>
                🏷️ Filtri Tipi Evento:
              </h3>
              {isLoadingEventTypes && (
                <span className={`text-xs ${mutedText}`}>Caricamento...</span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-3">
              {eventTypes.map((eventType) => {
                const isVisible = visibleEventTypes.has(eventType.id);
                const typeColor = eventType.color || '#6B7280';
                
                return (
                  <label 
                    key={eventType.id}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200
                      ${
                        isVisible 
                          ? 'bg-white dark:bg-gray-700 border-2 shadow-sm' 
                          : 'bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 opacity-60'
                      }
                    `}
                    style={{
                      borderColor: isVisible ? typeColor : undefined,
                      backgroundColor: isVisible ? `${typeColor}10` : undefined
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isVisible}
                      onChange={() => onEventTypeToggle && onEventTypeToggle(eventType.id)}
                      className="w-4 h-4 rounded focus:ring-2"
                      style={{
                        accentColor: typeColor
                      }}
                    />
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: typeColor }}
                    />
                    <span className={`text-sm font-medium ${
                      isVisible ? textColor : mutedText
                    }`}>
                      {eventType.name || eventType.description}
                    </span>
                  </label>
                );
              })}
            </div>
            
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              📝 Deseleziona i tipi evento per nasconderli dalla vista mensile
            </div>
          </div>
        )}

        {/* View-specific info */}
        <div className="mt-3 text-sm">
          {currentView === 'week' && (
            <p className={mutedText}>
              📅 Vista dettagliata con timeline oraria e gestione eventi
            </p>
          )}
          {currentView === 'month' && (
            <p className={mutedText}>
              📋 Seleziona più giorni per creare eventi multi-giorno (ferie, permessi)
            </p>
          )}
          {currentView === 'year' && (
            <p className={mutedText}>
              �️ Panoramica annuale per pianificazione a lungo termine
            </p>
          )}
        </div>
      </div>
      
      {/* Pannello statistiche mensili compatto (solo per vista mensile) */}
      {currentView === 'month' && (
        <MonthlyStatsHeader 
          year={selectedDate.getFullYear()}
          month={selectedDate.getMonth() + 1}
        />
      )}
    </div>
  );
};

export default CalendarHeader;